import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import axios from 'axios';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import DataGridPager from '~components/DataGrid/DataGridPager';
import actionsColumn from '~components/DataTable/actionsColumn';
import DataTable from '~components/DataTable/DataTable';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { ShakyErrorIndicator } from '~components/ErrorIndicators/ShakyErrorIndicator';
import LabelChip from '~components/Labels/LabelChip';
import { SimpleMenu } from '~components/Menu/SimpleMenu';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { DEFAULT_API_PAGE_SIZE } from '~constants/consts';
import { Label } from '~hooks/useLabels/models';
import useLabels from '~hooks/useLabels/useLabels';
import useLatestRequestSignal from '~hooks/useLatestRequestSignal/useLatestRequestSignal';
import SettingsPageHeader from '~pages/Settings/components/SettingsPageHeader';
import { Pagination, PaginationLink } from '~services/pagination';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { dateFormat } from '~utils/dateTime';
import { getPageNumber } from '~utils/pagination';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import LabelForm, { LabelFormHandler } from './LabelForm';
import { LabelDto, LabelDtoAsyncError } from './labelSchema';

type LabelDialogState = {
  label?: Label;
  isOpen: boolean;
  isSubmitting: boolean;
};

export const LabelsManagement = observer(() => {
  const { userStore, toasterStore } = useStores();

  const companyId = getEffectiveUserCompanyId(userStore);

  const { fetchLabels, createLabel, updateLabel, deleteLabel } = useLabels();
  const issueRequestSignal = useLatestRequestSignal();

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [searchValue, setSearchValue] = useState('');
  const [labels, setLabels] = useState<Label[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    limit: DEFAULT_API_PAGE_SIZE,
    page: 1,
  });

  const labelFormRef = useRef<LabelFormHandler>(null);
  const [labelFormDialogState, setLabelFormDialogState] = useState<LabelDialogState>({
    isOpen: false,
    isSubmitting: false,
  });
  const [labelFormDialogAsyncErrors, setLabelFormDialogAsyncErrors] = useState<
    LabelDtoAsyncError[]
  >([]);

  const [deleteLabelDialogState, setDeleteLabelDialogState] = useState<LabelDialogState>({
    isOpen: false,
    isSubmitting: false,
  });

  const fetchData = useCallback(
    async (link?: PaginationLink) => {
      const cursor = link ? pagination[link] : undefined;

      const signal = issueRequestSignal();
      setIsLoading(true);

      try {
        const result = await fetchLabels({
          companyId,
          link,
          cursor,
          search: searchValue,
          signal,
        });
        if (signal.aborted) return;

        if (result) {
          setLabels(result.data);
          setPagination({
            ...result.pagination,
            limit: pagination.limit,
            page: link ? getPageNumber(pagination.page, link) : 1,
          });
        }
      } catch (error) {
        if (signal.aborted) return;

        throw error;
      } finally {
        if (!signal.aborted) {
          setIsLoading(false);
        }
      }
    },
    [companyId, issueRequestSignal, pagination, searchValue],
  );

  useEffect(() => {
    fetchData();
  }, [companyId, searchValue]);

  const userPermissions = userStore.getPermissions();

  const handleCreateNewLabelClick = useCallback(() => {
    setLabelFormDialogState({ isOpen: true, isSubmitting: false });
  }, []);

  const handleDeleteLabelClick = useCallback((label: Label) => {
    setDeleteLabelDialogState({
      isOpen: true,
      isSubmitting: false,
      label,
    });
  }, []);

  const handleCloseLabelFormDialog = useCallback(() => {
    setLabelFormDialogState((state) => ({ ...state, isOpen: false }));
  }, []);

  const handleCloseDeleteLabelDialog = useCallback(() => {
    setDeleteLabelDialogState((state) => ({
      ...state,
      isOpen: false,
    }));
  }, []);

  const handleSubmitLabelFormDialog = useCallback(async () => {
    const formData = await labelFormRef.current?.onSubmit();

    if (!formData) {
      return;
    }

    const labelId = labelFormDialogState.label?.id;

    try {
      setLabelFormDialogAsyncErrors([]);
      setLabelFormDialogState((state) => ({
        ...state,
        isSubmitting: true,
      }));

      setIsSaving(true);

      if (labelId) {
        await updateLabel({
          companyId,
          labelId,
          label: { name: formData.name, color: formData.color },
        });

        toasterStore.push(alert(t('administration.labels.updated'), AlertTypes.success));
      } else {
        await createLabel({
          companyId,
          label: { name: formData.name, color: formData.color },
        });

        toasterStore.push(alert(t('administration.labels.created'), AlertTypes.success));
      }

      setLabelFormDialogState({ isOpen: false, isSubmitting: false });
      fetchData();
    } catch (error) {
      setLabelFormDialogState((state) => ({
        ...state,
        isSubmitting: false,
      }));

      if (axios.isAxiosError(error)) {
        const errorsArray = (error.response?.data?.error?.errors ?? []) as Array<{
          field: string;
          message: string;
        }>;

        const asyncErrors: LabelDtoAsyncError[] = [];
        const formFieldInfoByFieldInError: Record<
          string,
          { fieldInForm: keyof LabelDto; label: string }
        > = {
          name: {
            fieldInForm: 'name',
            label: t('form_fields.name'),
          },
        };

        errorsArray.forEach((errorsArrayItem) => {
          const { field, message } = errorsArrayItem;

          const isValueTakenError = message.includes('has already been taken');
          const fieldInfo = formFieldInfoByFieldInError[field];

          if (isValueTakenError && fieldInfo) {
            asyncErrors.push({
              field: fieldInfo.fieldInForm,
              message: t('error_messages.field_taken', {
                field: fieldInfo.label,
              }),
            });
          }
        });

        if (asyncErrors.length) {
          setLabelFormDialogAsyncErrors(asyncErrors);
        }
      }
    } finally {
      setIsSaving(false);
    }
  }, [labelFormDialogState.label?.id, companyId, fetchData]);

  const handleDeleteLabel = useCallback(async () => {
    const labelId = deleteLabelDialogState.label?.id;

    if (!labelId) {
      return;
    }

    setIsSaving(true);

    try {
      setDeleteLabelDialogState((state) => ({
        ...state,
        isSubmitting: true,
      }));

      await deleteLabel({ companyId, labelId });

      toasterStore.push(alert(t('administration.labels.deleted'), AlertTypes.success));
      setDeleteLabelDialogState({ isOpen: false, isSubmitting: false });
      fetchData();
    } catch {
      setDeleteLabelDialogState((state) => ({
        ...state,
        isSubmitting: false,
      }));
    } finally {
      setIsSaving(false);
    }
  }, [deleteLabelDialogState.label?.id, companyId, fetchData]);

  const columns = useMemo<Array<DataTableColumn<Label>>>(
    () => [
      {
        field: 'name',
        headerName: t('form_fields.name'),
        renderCell: (label) => <LabelChip name={label.name} color={label.color} />,
      },
      {
        field: 'color',
        headerName: t('form_fields.color'),
        renderCell: (label) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                backgroundColor: label.color,
              }}
            />
            {label.color}
          </Box>
        ),
      },
      {
        field: 'createdAt',
        headerName: t('common.created_on'),
        renderCell: (label) =>
          label.createdAt ? (
            <Tooltip
              title={`${t('common.last_update')}: ${label.updatedAt ? dateFormat(label.updatedAt) : '-'}`}
            >
              <span>{dateFormat(label.createdAt)}</span>
            </Tooltip>
          ) : null,
      },
      actionsColumn((label) => {
        const options = userPermissions.canDeleteLabel
          ? [
              {
                title: t('actions.delete'),
                icon: <Delete />,
                callBack: () => handleDeleteLabelClick(label),
              },
            ]
          : [];
        return (
          <>
            {userPermissions.canEditLabel && (
              <IconButton
                size="small"
                onClick={() => {
                  setLabelFormDialogState({
                    isOpen: true,
                    isSubmitting: false,
                    label,
                  });
                }}
              >
                <Edit />
              </IconButton>
            )}
            {options.length > 0 && (
              <SimpleMenu options={options}>
                <MoreHoriz />
              </SimpleMenu>
            )}
          </>
        );
      }),
    ],
    [userPermissions.canEditLabel, userPermissions.canDeleteLabel],
  );

  useEffect(() => {
    if (!labelFormDialogState.isOpen) {
      setLabelFormDialogAsyncErrors([]);
    }
  }, [labelFormDialogState.isOpen]);

  return (
    <>
      <SettingsPageHeader
        title={t('administration.labels.title')}
        description={t('settings.page_descriptions.labels')}
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <SmallButton
          color="brandV2Yellow"
          disabled={!userPermissions.canCreateLabel}
          onClick={handleCreateNewLabelClick}
          startIcon={<Add />}
        >
          {t('administration.labels.create')}
        </SmallButton>
      </Box>
      <Box data-test-id={'labels-data-grid'}>
        <DataTable
          id="labels-data-grid"
          columns={columns}
          rows={labels}
          loading={isLoading || isSaving}
          onSearchChange={setSearchValue}
          headerTrailingComponent={
            <DataGridPager
              isFetching={isLoading}
              onNavigate={fetchData}
              pagination={pagination}
            />
          }
        />
      </Box>
      <Dialog
        isOpen={labelFormDialogState.isOpen}
        preventCloseOnBackdropClick
        preventCloseOnEscapeKeyDown
      >
        <DialogHeader onClose={handleCloseLabelFormDialog}>
          {labelFormDialogState.label
            ? t('administration.labels.update')
            : t('administration.labels.create')}
        </DialogHeader>

        <DialogContent>
          <LabelForm
            ref={labelFormRef}
            asyncErrors={labelFormDialogAsyncErrors}
            label={labelFormDialogState.label}
          />
        </DialogContent>

        <DialogActions>
          {Boolean(labelFormDialogAsyncErrors.length) && (
            <ShakyErrorIndicator
              message={t('administration.users.user_form.invalid_data_error_messages')}
            />
          )}

          <SmallButton
            color="secondary"
            disabled={labelFormDialogState.isSubmitting}
            onClick={handleCloseLabelFormDialog}
            variant="outlined"
          >
            {t('actions.cancel')}
          </SmallButton>

          <SmallLoadingButton
            loading={labelFormDialogState.isSubmitting}
            onClick={handleSubmitLabelFormDialog}
          >
            {labelFormDialogState.label ? t('actions.update') : t('actions.create')}
          </SmallLoadingButton>
        </DialogActions>
      </Dialog>
      <ConfirmationDialog
        isBusy={deleteLabelDialogState.isSubmitting}
        isOpen={deleteLabelDialogState.isOpen}
        onClose={handleCloseDeleteLabelDialog}
        onConfirm={handleDeleteLabel}
        title={t('administration.labels.delete')}
      >
        <Typography>{t('administration.labels.delete_confirmation')}</Typography>
      </ConfirmationDialog>
    </>
  );
});
